import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  Users,
  Package,
  Settings,
  LogOut,
  Menu,
  Home,
  Globe,
  UserPlus,
  Building2,
  History,
  HandHeart,
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: Home, roles: ['admin', 'operator'] },
  { path: '/beneficiaries', label: 'Beneficiaries', icon: Users, roles: ['admin', 'operator'] },
  { path: '/distribution', label: 'Distribution', icon: Package, roles: ['admin', 'operator'] },
  { path: '/distribution-history', label: 'History', icon: History, roles: ['admin', 'operator'] },
  { path: '/shared-portal', label: 'Shared Portal', icon: Globe, roles: ['admin', 'operator'] },
  { path: '/waiting-list', label: 'Waiting List', icon: HandHeart, roles: ['admin', 'operator'] },
  { path: '/users', label: 'User Management', icon: UserPlus, roles: ['admin'] },
  { path: '/settings', label: 'Settings', icon: Settings, roles: ['admin'] },
];

export default function Layout({ children }) {
  const { user, organization, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-[#E5E5E5]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#4A5D4E] flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-[#1A1A1A] font-['Manrope']">ATHINAPPY</h1>
            <p className="text-xs text-[#8C8C8C] truncate max-w-[140px]">
              {organization?.name || 'Organization'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-3 space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      {/* User info & logout */}
      <div className="p-4 border-t border-[#E5E5E5]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#4A5D4E] flex items-center justify-center text-white text-sm font-medium">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#1A1A1A] truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-[#8C8C8C] capitalize">{user?.role}</p>
          </div>
        </div>
        <Button
          variant="outline"
          className="w-full justify-start gap-2 text-[#595959] hover:text-[#D34848] hover:border-[#D34848]"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 sidebar">
        <NavContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E5E5E5] z-40 flex items-center px-4">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64 sidebar">
            <NavContent />
          </SheetContent>
        </Sheet>
        <div className="ml-4 flex items-center gap-2">
          <Building2 className="w-6 h-6 text-[#4A5D4E]" />
          <span className="font-bold text-[#1A1A1A] font-['Manrope']">ATHINAPPY</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen pb-20 lg:pb-0">
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
